import React from "react";

/**
 * Initials-with-colored-background fallback for avatars where we know the
 * entity's display name but don't have an image. Colour is chosen
 * deterministically from the name via djb2-ish hashing so the same name
 * always renders the same swatch across sessions and users — no flicker
 * on re-render, no re-coloring when the same space is opened again.
 *
 * Palette is restricted to Tailwind 2.x colors/shades known to be present
 * in this project (the extended palette — slate/zinc/neutral/orange/etc. —
 * was added in Tailwind 3).
 */

const PALETTE = [
  "bg-red-500",
  "bg-pink-500",
  "bg-purple-500",
  "bg-indigo-500",
  "bg-blue-500",
  "bg-green-500",
  "bg-yellow-500",
  "bg-red-400",
  "bg-pink-400",
  "bg-purple-400",
  "bg-indigo-400",
  "bg-blue-400",
  "bg-green-400",
];

const hashString = (input) => {
  let h = 0;
  if (!input) return 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0; // coerce to 32-bit int
  }
  return Math.abs(h);
};

export const pickInitialsBackground = (name) =>
  PALETTE[hashString(name || "") % PALETTE.length];

const extractInitial = (name) => {
  const trimmed = (name || "").trim();
  if (!trimmed) return "?";
  // Skip any leading emoji/symbol characters and grab the first letter.
  const letter = trimmed.match(/[\p{L}\p{N}]/u)?.[0];
  return (letter || trimmed.charAt(0)).toUpperCase();
};

const InitialsFallback = ({ name, dimensions = "w-12 h-12", className = "" }) => {
  const initial = extractInitial(name);
  const bg = pickInitialsBackground(name);

  return (
    <div
      role="img"
      aria-label={name || "Avatar"}
      className={`flex items-center justify-center rounded-full text-white ${bg} ${dimensions} ${className}`}
    >
      <span className="select-none font-semibold uppercase leading-none">
        {initial}
      </span>
    </div>
  );
};

export default InitialsFallback;
